/**
 * MemberPortalPage
 * ================
 * Public church-member self-service portal. Two states:
 *
 *   1. Logged-out:  shows tabs for "Sign in" (existing claimed account)
 *                   and "Claim my account" (first-time activation using the
 *                   member id printed on the welcome letter / member card).
 *
 *   2. Logged-in:   renders the unified My Giving dashboard
 *                   (`<MemberGivingDashboard embedded />`) wired to the
 *                   member self endpoints, plus a sign-out button.
 *
 * Token lives in `localStorage["member_portal_token"]`. We deliberately
 * don't use a HttpOnly cookie here because the dashboard is fetched from
 * the same hostname (no CSRF surface) and the rest of this app already
 * stores its JWTs in localStorage.
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { LogOut, Heart, Loader2, FileDown, Mail, UserSquare2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'member_portal_token';

const apiFetch = async (path, opts = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${r.status})`);
  }
  return r.json();
};

const MemberPortalPage = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');

  // If we have a token, hit /me to confirm it's still valid (catches expired
  // sessions on page refresh). Failure clears the token so the login UI shows.
  const { data: me, isError } = useQuery({
    queryKey: ['member-portal-me', token],
    queryFn: () => apiFetch('/api/church/members/portal/me'),
    enabled: !!token,
    retry: false,
  });
  useEffect(() => {
    if (isError && token) {
      localStorage.removeItem(TOKEN_KEY);
      setToken('');
    }
  }, [isError, token]);

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    toast.success('Signed out');
  };

  if (!token || !me) {
    return (
      <AuthPanel
        onAuthed={(t) => {
          localStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">{me.first_name} {me.last_name}</p>
            <p className="text-[11px] text-slate-500">{me.church_name || 'Member portal'}</p>
          </div>
          <Button size="sm" variant="outline" onClick={signOut} data-testid="member-portal-signout">
            <LogOut className="h-4 w-4 mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        <MyBioPanel />
        <MyGivingPanel />
      </main>
    </div>
  );
};

/* ────────────────────── My public bio ────────────────────── */

/** Leaders publish a short bio and photo to the church page — once a church
 *  admin has approved it. Hidden for members without a church role. */
const MyBioPanel = () => {
  const qc = useQueryClient();
  const [bio, setBio] = useState('');
  const [touched, setTouched] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data } = useQuery({
    queryKey: ['member-my-bio'],
    queryFn: () => apiFetch('/api/church/members/portal/me/bio'),
    retry: false,
  });

  useEffect(() => {
    if (data && !touched) setBio(data.draft_bio || data.bio || '');
  }, [data, touched]);

  const submit = useMutation({
    mutationFn: () => apiFetch('/api/church/members/portal/me/bio', {
      method: 'PUT', body: JSON.stringify({ bio }),
    }),
    onSuccess: () => {
      toast.success('Sent for approval', { description: 'A church admin will publish it shortly.' });
      setTouched(false);
      qc.invalidateQueries({ queryKey: ['member-my-bio'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const token = localStorage.getItem(TOKEN_KEY);
      const r = await fetch(`${API_URL}/api/church/members/portal/me/bio/photo`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body,
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Upload failed');
      toast.success('Photo sent for approval');
      qc.invalidateQueries({ queryKey: ['member-my-bio'] });
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };

  if (!data?.eligible) return null;

  const max = data.max_length || 600;
  const photo = data.draft_photo_url || data.photo_url;

  return (
    <Card data-testid="member-bio-panel">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="flex items-center gap-2 font-semibold text-slate-900">
              <UserSquare2 className="h-4 w-4 text-emerald-700" /> My public bio
            </p>
            <p className="text-[11px] text-slate-500">
              Shown on the church page next to your name and role ({(data.roles || []).join(' · ')}).
            </p>
          </div>
          <span data-testid="member-bio-status"
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                  data.status === 'approved' ? 'bg-emerald-50 text-emerald-700'
                    : data.status === 'pending' ? 'bg-amber-50 text-amber-700'
                    : data.status === 'rejected' ? 'bg-rose-50 text-rose-700'
                    : 'bg-slate-100 text-slate-500'}`}>
            {data.status === 'approved' ? 'Published'
              : data.status === 'pending' ? 'Waiting for approval'
              : data.status === 'rejected' ? 'Sent back for changes'
              : 'Not written yet'}
          </span>
        </div>

        {data.status === 'rejected' && data.review_note && (
          <p className="rounded-md bg-rose-50 p-2 text-xs text-rose-700" data-testid="member-bio-note">
            Your church admin asked: {data.review_note}
          </p>
        )}

        <div className="flex items-center gap-3">
          {photo ? (
            <img src={photo} alt="Your photo" className="h-16 w-16 rounded-full object-cover" />
          ) : (
            <span className="grid h-16 w-16 place-items-center rounded-full bg-slate-100 text-slate-400">
              <UserSquare2 className="h-6 w-6" />
            </span>
          )}
          <label className="cursor-pointer text-xs font-medium text-emerald-700 hover:underline">
            {uploading ? 'Uploading…' : 'Upload a photo'}
            <input type="file" accept="image/*" className="hidden" data-testid="member-bio-photo-input"
                   onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          </label>
        </div>

        <div>
          <Label htmlFor="member-bio">Bio</Label>
          <textarea id="member-bio" rows={5} value={bio} maxLength={max}
                    data-testid="member-bio-textarea"
                    onChange={(e) => { setBio(e.target.value); setTouched(true); }}
                    placeholder="A sentence or two about your ministry and family."
                    className="w-full rounded-md border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
          <p className="text-right text-[11px] text-slate-400">{bio.length}/{max}</p>
        </div>
        <Button size="sm" onClick={() => submit.mutate()}
                disabled={submit.isPending || bio.trim().length < 20}
                data-testid="member-bio-submit">
          {submit.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          Send for approval
        </Button>
      </CardContent>
    </Card>
  );
};

/* ────────────────────── Auth screen ────────────────────── */

const AuthPanel = ({ onAuthed }) => {
  const [mode, setMode] = useState('login');
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 grid place-items-center p-6">
      <Card className="w-full max-w-md shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="h-6 w-6 text-rose-500" />
            <h1 className="text-xl font-bold text-slate-900">Member portal</h1>
          </div>
          <p className="text-xs text-slate-500 mb-4">View your giving history, statements and member profile.</p>
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="member-tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="claim" data-testid="member-tab-claim">Claim my account</TabsTrigger>
            </TabsList>
            <TabsContent value="login" className="mt-4">
              <LoginForm onAuthed={onAuthed} />
            </TabsContent>
            <TabsContent value="claim" className="mt-4">
              <ClaimForm onAuthed={onAuthed} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

const LoginForm = ({ onAuthed }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await apiFetch('/api/church/members/portal/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      onAuthed(d.token);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="member-login-form">
      <div>
        <Label>Email</Label>
        <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="member-login-email" />
      </div>
      <div>
        <Label>Password</Label>
        <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} data-testid="member-login-password" />
      </div>
      <Button type="submit" className="w-full" disabled={busy} data-testid="member-login-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
        Sign in
      </Button>
    </form>
  );
};

const ClaimForm = ({ onAuthed }) => {
  /**
   * 2-step OTP-based account claim:
   *   1. Member enters email → we ask the backend to email a 6-digit code.
   *   2. They enter the code + a new password → we activate the account.
   */
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      await apiFetch('/api/church/members/portal/request-otp', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      toast.success("If your email is on file, we've sent you a 6-digit code.");
      setStep(2);
      setCooldown(60);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    if (!/^\d{6}$/.test(code)) return toast.error('Please enter the 6-digit code');
    setBusy(true);
    try {
      const d = await apiFetch('/api/church/members/portal/claim', {
        method: 'POST',
        body: JSON.stringify({ email, activation_code: code, new_password: password }),
      });
      toast.success('Account activated');
      onAuthed(d.token);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (step === 1) {
    return (
      <form onSubmit={requestOtp} className="space-y-3" data-testid="member-claim-form">
        <div>
          <Label>Email on file</Label>
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="member-claim-email"
          />
          <p className="text-[11px] text-slate-500 mt-1">We'll email a 6-digit code to confirm it's you.</p>
        </div>
        <Button type="submit" className="w-full" disabled={busy || !email} data-testid="member-claim-request-otp">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}
          Send me a code
        </Button>
      </form>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="member-claim-form">
      <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2">
        Code sent to <span className="font-mono">{email}</span>.{' '}
        <button
          type="button"
          className="text-emerald-700 hover:underline disabled:opacity-50"
          disabled={cooldown > 0 || busy}
          onClick={() => requestOtp()}
          data-testid="member-claim-resend"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </button>
      </div>
      <div>
        <Label>6-digit code</Label>
        <Input
          required
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          className="font-mono tracking-widest text-lg text-center"
          data-testid="member-claim-code"
        />
        <p className="text-[10px] text-slate-400 mt-1">Code expires in 15 minutes. Check your spam folder if you don't see it.</p>
      </div>
      <div>
        <Label>Choose a password</Label>
        <Input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="member-claim-password"
        />
      </div>
      <Button type="submit" className="w-full" disabled={busy} data-testid="member-claim-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
        Activate &amp; sign in
      </Button>
      <button
        type="button"
        className="text-[11px] text-slate-500 hover:underline"
        onClick={() => { setStep(1); setCode(''); }}
      >
        ← Change email
      </button>
    </form>
  );
};

/* ─────────────────── Giving panel ─────────────────── */

const MyGivingPanel = () => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['member-portal-my-giving'],
    queryFn: () => apiFetch('/api/church/members/portal/me/giving'),
  });
  if (isLoading) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (isError) return <p className="text-sm text-red-600">{error.message}</p>;

  // Reuse the same renderer as the admin dashboard so we have one source of
  // truth for what My Giving looks like. We import it lazily to avoid a
  // circular path with `MemberGivingDashboard` and its admin parent.
  const fmt = (n, ccy = '') => {
    const v = Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return ccy ? `${ccy} ${v}` : `R${v}`;
  };
  const cats = Object.entries(data?.by_category || {});
  const total = cats.reduce((s, [, v]) => s + (v.total || 0), 0) || 1;

  return (
    <div className="space-y-5" data-testid="member-portal-giving">
      <div>
        <h2 className="text-xl font-bold text-slate-900">My Giving</h2>
        <p className="text-xs text-slate-500">Pulled directly from the unified accounting ledger.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-[10px] text-slate-500 uppercase">Lifetime</p>
          <p className="text-xl font-bold text-slate-900" data-testid="mp-lifetime">{fmt(data?.totals?.lifetime)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-[10px] text-slate-500 uppercase">This year</p>
          <p className="text-xl font-bold text-slate-900" data-testid="mp-this-year">{fmt(data?.totals?.this_year)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-[10px] text-slate-500 uppercase">Last year</p>
          <p className="text-xl font-bold text-slate-900">{fmt(data?.totals?.last_year)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-[10px] text-slate-500 uppercase">Gifts</p>
          <p className="text-xl font-bold text-slate-900">{data?.totals?.count || 0}</p>
        </CardContent></Card>
      </div>
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-semibold text-slate-800 mb-3">By category</p>
          {cats.length === 0 ? (
            <p className="text-xs text-slate-400">No giving recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {cats.sort(([, a], [, b]) => b.total - a.total).map(([k, v]) => {
                const pct = ((v.total / total) * 100).toFixed(1);
                return (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-24 text-xs text-slate-700 capitalize">{k}</span>
                    <div className="flex-1 h-3 bg-slate-100 rounded overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-28 text-right text-xs font-mono">{fmt(v.total)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-semibold text-slate-800 mb-3">Recent activity</p>
          {!data?.recent?.length ? (
            <p className="text-xs text-slate-400">No recent contributions.</p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {data.recent.map(r => (
                <div key={r.id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-800 capitalize">{r.category || 'other'} <span className="text-slate-400">· {r.method || 'unknown'}</span></p>
                    <p className="text-[10px] text-slate-400">{r.date && new Date(r.date).toLocaleDateString()}</p>
                  </div>
                  <p className="text-sm font-semibold text-emerald-700 tabular-nums">{fmt(r.amount, r.currency)}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <YearEndStatementCard />
    </div>
  );
};

/* ─────────────── Year-end statement download ─────────────── */

const YearEndStatementCard = () => {
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4];
  const [year, setYear] = useState(currentYear - 1);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const r = await fetch(`${API_URL}/api/church/members/portal/me/giving/statement.pdf?year=${year}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        const errBody = await r.text().catch(() => '');
        throw new Error(`Server returned ${r.status}${errBody ? ': ' + errBody.slice(0, 120) : ''}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `giving-statement-${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Statement for ${year} downloaded`);
    } catch (err) {
      toast.error(err.message || 'Could not generate statement');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <FileDown className="h-4 w-4 text-emerald-600" />
            Year-end giving statement
          </p>
          <p className="text-xs text-slate-500 mt-1">PDF receipt of every contribution for the selected tax year. Used for personal records and tax-deductible giving claims.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="text-xs border rounded px-2 py-1.5 bg-white"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            data-testid="member-statement-year"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button onClick={download} disabled={busy} size="sm" data-testid="member-statement-download">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileDown className="h-4 w-4 mr-1" />}
            Download
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MemberPortalPage;
